import React, { useEffect } from 'react';
import { FormProvider, useForm, useFieldArray } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';

import useToastPromise from '../../../../../hooks/useToast';
import dragAndDropIcon from '../../../../../styles/svg/workflowStepdotsIcon.svg';
import { CustomButton } from '../../../../../components/Button/Button';
import ManageFieldsLeft from './ManageFieldsLeft';
import ManageFieldsRight from './ManageFieldsRight';
import { useGetBoardDynamicFields } from '../../../../../utils/reactQuery/users';
import { addDynamicFields } from '../../../../../schemas/addDynamicFields';
import { addCustomField, deleteCustomField, updateCustomFieldSequence } from '../../../../../sevices/apiUser';
import useBoardIdByKey from '../../../../../hooks/useBoardIdByKey';

const FIELD_TYPES = [
    { value: '', label: 'Field Type' },
    { value: 'text', label: 'Textbox' },
    { value: 'multi_choice', label: 'Multiple Choice' },
    { value: 'checkbox', label: 'Multi Checkbox' },
    { value: 'select', label: 'Dropdown' },
    { value: 'date', label: 'Date' },
    { value: 'number', label: 'Number' },
];

const ManageFieldsForm = ({ currentBoard, onClose }) => {
    const queryClient = useQueryClient();
    const { showToastPromise } = useToastPromise();
    const { id: paramBoardId } = useBoardIdByKey();
    const { data: customFieldData, isLoading: loadingCustomField, isRefetching } = useGetBoardDynamicFields(paramBoardId);
    const methods = useForm({
        defaultValues: {
            display_name: '',
            field_type: '',
            sequence: '',
            fields: customFieldData?.data?.data?.responseData?.fields.map(item => ({
                name: item.display_name,
                sequence: item.sequence,
                custom_field_id: item?.custom_field_id,
                field_type: item?.field_type
            }))
        },
        resolver: yupResolver(addDynamicFields),
    });

    useEffect(() => {
        if (customFieldData?.data?.data?.responseData?.fields) {
            const mappedFields = customFieldData.data.data.responseData.fields.map(item => ({
                name: item.display_name,
                sequence: item.sequence,
                custom_field_id: item?.custom_field_id,
                field_type: item?.field_type
            }));
            methods.setValue('fields', mappedFields);
        }
    }, [loadingCustomField, isRefetching]);

    const { handleSubmit, control, watch } = methods;
    const label = watch('display_name');
    const fieldType = watch('field_type');
    const { fields, move } = useFieldArray({
        control,
        name: 'fields',
    });

    const { mutate, isPending } = useMutation({
        mutationFn: async (data) => {
            let fieldsWithSequence = {};
            const sequence = parseInt(data.sequence.split('_')[0]);

            if (data.sequence.split('_')[1] === 'before') {
                fieldsWithSequence = fields.reduce((acc, field, index) => {
                    if (sequence === 1) {
                        acc[field.name] = index + 2;
                    } else {
                        if (index + 1 > sequence) {
                            acc[field.name] = index + 2;
                        } else {
                            acc[field.name] = index + 1;
                        }
                    }
                    return acc;
                }, {});
                fieldsWithSequence[data.display_name] = sequence === 1 ? 1 : parseInt(data.sequence.split('_')[0]) + 1;
            } else if (data.sequence.split('_')[1] === 'after') {
                fieldsWithSequence = fields.reduce((acc, field, index) => {
                    const sequence = parseInt(data.sequence.split('_')[0]);
                    if (index + 1 >= sequence) {
                        acc[field.name] = index + 2;
                    } else {
                        acc[field.name] = index + 1;
                    }
                    return acc;
                }, {});
                fieldsWithSequence[data.display_name] = parseInt(data.sequence.split('_')[0]);
            }

            const payload = data?.settings
                ? { display_name: data.display_name, field_type: data.field_type, sequence: fieldsWithSequence, settings: data.settings.map(item => (item.value)) }
                : { display_name: data.display_name, field_type: data.field_type, sequence: fieldsWithSequence };
            await showToastPromise(() => addCustomField(paramBoardId, payload), payload);
        },
        onSuccess: () => {
            methods.reset({
                display_name: '',
                field_type: '',
                settings: []
            });
            queryClient.invalidateQueries({ queryKey: ['getCustomFields', paramBoardId] });
            queryClient.invalidateQueries({ 
                predicate: (query) => query.queryKey[0] === 'card'
            });
        },
    });

    const { mutate: mutateCustomFieldSequence, isPending: isLoadingUpdateCustomFieldSequence } = useMutation({
        mutationFn: async (data) => {
            await showToastPromise(() => updateCustomFieldSequence(paramBoardId, data));
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['getCustomFields', paramBoardId] });
        },
    });

    const { mutate: mutateDeleteCustomField } = useMutation({
        mutationFn: async (fieldId) => {
            await showToastPromise(() => deleteCustomField(paramBoardId, fieldId));
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['getCustomFields', paramBoardId] });
        },
        onError: (error) => {
            // toast.error(error?.response?.data?.message);
        },
    });

    const handleDeleteCustomField = (customFieldId) => {
        mutateDeleteCustomField(customFieldId);
    };

    const onSave = () => {
        const fieldOrder = {};
        fields.forEach((field, index) => {
            fieldOrder[field.name] = index + 1;
        });
        mutateCustomFieldSequence({ sequence: JSON.stringify(fieldOrder) });
    };

    const onSubmit = (data) => {
        mutate(data);
    };

    const onDragEnd = (result) => {
        if (!result.destination) return;
        move(result.source.index, result.destination.index);
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)} className="manage-fields-form-root">
                <ManageFieldsLeft
                    methods={methods}
                    label={label}
                    fieldType={fieldType}
                    FIELD_TYPES={FIELD_TYPES}
                    fields={fields}
                    isLoading={isPending}
                />
                <ManageFieldsRight
                    fields={fields}
                    onDragEnd={onDragEnd}
                    dragAndDropIcon={dragAndDropIcon}
                    onClose={onClose}
                    CustomButton={CustomButton}
                    onSave={onSave}
                    isLoading={isLoadingUpdateCustomFieldSequence}
                    handleDeleteCustomField={handleDeleteCustomField}
                />
            </form>
        </FormProvider>
    );
};

export default ManageFieldsForm; 